import { create } from 'zustand'
import type { IndexCard, CreateIndexCardInput, UpdateIndexCardInput } from '@/types'
import { api } from '@/services/api'

interface IndexCardsState {
  indexCards: Map<string, IndexCard>
  isLoading: boolean
  error: string | null
}

interface IndexCardsActions {
  // Local state actions
  setIndexCards: (indexCards: IndexCard[]) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void

  // API actions
  fetchIndexCards: () => Promise<void>
  addIndexCard: (data: CreateIndexCardInput) => Promise<IndexCard>
  updateIndexCard: (id: string, data: UpdateIndexCardInput) => Promise<void>
  deleteIndexCard: (id: string) => Promise<void>
  addReference: (id: string, lid: string) => Promise<void>
  removeReference: (id: string, lid: string) => Promise<void>

  // Computed getters
  getIndexCard: (id: string) => IndexCard | undefined
  getIndexCardsSorted: () => IndexCard[]
  getIndexCardCount: () => number
  searchIndexCards: (query: string) => IndexCard[]
}

type IndexCardsStore = IndexCardsState & IndexCardsActions

export const useIndexCardsStore = create<IndexCardsStore>((set, get) => ({
  // Initial state
  indexCards: new Map(),
  isLoading: true,
  error: null,

  // Local state actions
  setIndexCards: (indexCards) => {
    const map = new Map<string, IndexCard>()
    indexCards.forEach((ic) => map.set(ic.id, ic))
    set({
      indexCards: map,
      isLoading: false,
      error: null,
    })
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  // API actions
  fetchIndexCards: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.getIndexCards()
      const map = new Map<string, IndexCard>()
      ;(response?.indexCards ?? []).forEach((ic) => map.set(ic.id, ic))
      set({
        indexCards: map,
        isLoading: false,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch index cards'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  addIndexCard: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.createIndexCard(data)
      set((state) => {
        const newMap = new Map(state.indexCards)
        newMap.set(response.indexCard.id, response.indexCard)
        return { indexCards: newMap, isLoading: false }
      })
      return response.indexCard
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create index card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateIndexCard: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.updateIndexCard(id, data)
      set((state) => {
        const newMap = new Map(state.indexCards)
        newMap.set(id, response.indexCard)
        return { indexCards: newMap, isLoading: false }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update index card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteIndexCard: async (id) => {
    set({ isLoading: true, error: null })
    try {
      await api.deleteIndexCard(id)
      set((state) => {
        const newMap = new Map(state.indexCards)
        newMap.delete(id)
        return { indexCards: newMap, isLoading: false }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete index card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  addReference: async (id, lid) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.addIndexCardReference(id, lid)
      set((state) => {
        const newMap = new Map(state.indexCards)
        newMap.set(id, response.indexCard)
        return { indexCards: newMap, isLoading: false }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add reference'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  removeReference: async (id, lid) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.removeIndexCardReference(id, lid)
      set((state) => {
        const newMap = new Map(state.indexCards)
        newMap.set(id, response.indexCard)
        return { indexCards: newMap, isLoading: false }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove reference'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Computed getters
  getIndexCard: (id) => get().indexCards.get(id),

  getIndexCardsSorted: () => {
    const indexCards = Array.from(get().indexCards.values())
    return indexCards.sort((a, b) => a.keyword.localeCompare(b.keyword))
  },

  getIndexCardCount: () => get().indexCards.size,

  searchIndexCards: (query) => {
    const lowerQuery = query.toLowerCase()
    return Array.from(get().indexCards.values()).filter(
      (ic) =>
        ic.keyword.toLowerCase().includes(lowerQuery) ||
        ic.description?.toLowerCase().includes(lowerQuery)
    )
  },
}))
